<template>
  <button
    type="button"
    class="language-switch"
    :class="{ disabled: disabled }"
    :disabled="disabled"
    :aria-label="computedAriaLabel"
    :aria-hidden="disabled"
    tabindex="0"
    @click.stop.prevent="!disabled && toggleLocale()"
    @keydown.enter.stop.prevent="!disabled && toggleLocale()"
    @keydown.space.stop.prevent="!disabled && toggleLocale()"
  >
    <span v-if="!disabled">
      <span v-if="currentBase === 'en'">Cymraeg</span>
      <span v-else>English</span>
    </span>
  </button>
</template>

<script>
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

export default {
  name: "LanguageSwitch",

  props: {
    disabled: { type: Boolean, default: false },
  },

  setup(props) {
    const route = useRoute();
    const { locale, availableLocales } = useI18n({ useScope: "global" });
    const avail = availableLocales.map((s) => String(s).toLowerCase());

    const currentBase = computed(() => {
      const m = (route.path || "/").match(/^\/(en|cy)(\/|$)/);
      if (m) return m[1];

      const l = String(locale.value || "en").toLowerCase();
      return l.startsWith("cy") ? "cy" : "en";
    });

    const computedAriaLabel = computed(() =>
      props.disabled
        ? ""
        : currentBase.value === "cy"
        ? "Switch to English"
        : "Newid i’r Gymraeg"
    );

    function replacePrefix(path, targetBase) {
      const m = path.match(/^\/(en|cy)(\/|$)/);
      if (m) return path.replace(/^\/(en|cy)(?=\/|$)/, `/${targetBase}`);
      return path === "/" ? `/${targetBase}` : `/${targetBase}${path}`;
    }

    function resolveConcrete(base) {
      if (avail.includes(base)) return base;

      if (base === "en")
        return avail.find((l) => l === "en-gb" || l.startsWith("en-")) || "en";

      if (base === "cy")
        return (
          avail.find(
            (l) => l === "cy-gb" || l.startsWith("cy-") || l === "cy"
          ) || "cy"
        );

      return base;
    }

    function toggleLocale() {
      const targetBase = currentBase.value === "cy" ? "en" : "cy";
      const url = new URL(window.location.href);

      url.pathname = replacePrefix(route.path || "/", targetBase);
      url.searchParams.delete("lang");

      try {
        locale.value = resolveConcrete(targetBase);
      } catch (_) {
        // ignore – page will reload anyway
      }

      window.location.assign(url.toString());
    }

    return {
      props,
      locale,
      currentBase,
      computedAriaLabel,
      toggleLocale,
    };
  },
};
</script>

<style scoped>
.language-switch {
  border: 0;
  cursor: pointer;
  font: inherit;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 3px;
  transition: all ease 0.3s;
  font-family: "bebas neue";
  font-size: 1.1rem;
}

.language-switch.disabled {
  opacity: 0;
  pointer-events: none;
}
</style>
